import { useState } from "react";

// Services
import { getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { formatDateToISOString, validateFormDate } from "@/main/services/utilsDate";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { downloadFile } from "@/main/services/utilsFile";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";

// Types
import { ProcurementReportRequest, SearchGridShippingAndBillingRequest } from "@/main/models/request";

// Api
import { procurementReportApi } from "@/main/api";

// Redux
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";

// Components
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";

// Components/Styles Local
import ShippingCostGrid from "./components/shippingCostGrid/shippingCostGrid";
import BoxFiltersComponentes, {
	IGridFiltersData,
	INITIAL_GRID_FILTERS_STATE,
	IReportFiltersForm,
} from "../components/filters/boxFiltersComponentes";

function ShippingCostScreen() {
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultShippingCostReportInterval);

	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [loadingExportOrder, setLoadingExportOrder] = useState<boolean>(false);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);
	const [gridData, setGridData] = useState<IGridFiltersData>(INITIAL_GRID_FILTERS_STATE);
	const [formFilters, setFormFilters] = useState<any | null>(null);
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);

	function onSubmitSearch(dataForm: IReportFiltersForm) {
		validateFormDate(dataForm.startDate, dataForm.finalDate, parameterRangeDays);

		setPageIndex(PaginationEnum.PageIndexIntial);
		setLoadingSave(true);

		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const request: SearchGridShippingAndBillingRequest = removeEmptyValues({
			guidCountry: String(dataForm.country?.key) || "",
			guidBodyShop: String(dataForm.bodyshopBranch?.key) || "",
			guidInsurer: String(dataForm.insurer?.key) || "",
			guidSupplier: String(dataForm.supplier?.key) || "",
			initialDate: formatDateToISOString(dataForm.startDate),
			finalDate: formatDateToISOString(dataForm.finalDate),
			claimNr: dataForm.claimNr,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		});

		setFormFilters(request);

		procurementReportApi
			.searchGridShippingCost(request)
			.then((res) => {
				setGridData({
					isLoading: false,
					totalItems: res.data.totalNumberOfRecords,
					data: res.data.totalNumberOfRecords > 0 ? res.data.results : [],
				});
				setHasShowMore(res.data.results.length < res.data.totalNumberOfRecords);
			})
			.finally(() => {
				setLoadingSave(false);
			});

		setShowFilters(!showFilters);
	}

	function onSubmitExportOrder(dataForm: IReportFiltersForm) {
		validateFormDate(dataForm.startDate, dataForm.finalDate, parameterRangeDays);

		setLoadingExportOrder(true);

		const request: ProcurementReportRequest = removeEmptyValues({
			guidCountry: String(dataForm.country?.key) || "",
			guidBodyShop: String(dataForm.bodyshopBranch?.key) || "",
			guidInsurer: String(dataForm.insurer?.key) || "",
			guidSupplier: String(dataForm.supplier?.key) || "",
			initialDate: formatDateToISOString(dataForm.startDate),
			finalDate: formatDateToISOString(dataForm.finalDate),
			claimNr: dataForm.claimNr,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		});

		procurementReportApi
			.shippingCostReportExport(request)
			.then((res) => {
				const resData = res.data.result;
				const fileName = `${getTranslateByKey("information.shippingCostReport")}.xlsx`.split(" ").join("");
				downloadFile(resData, fileName);
			})
			.finally(() => {
				setLoadingExportOrder(false);
			});
	}

	// CONSTANTS LAYOUT
	const titleScreen = `${getTranslateByKey("module.report")} > ${getTranslateByKey("information.shippingCostReport")}`;

	return (
		<>
			<ContainerScreen hasBackground title={titleScreen}>
				<BoxFiltersComponentes
					loadingExportOrder={loadingExportOrder}
					showFilters={showFilters}
					setShowFilters={setShowFilters}
					loadingSave={loadingSave}
					setGridData={setGridData}
					setIsLoadingInfiniteScroll={setIsLoadingInfiniteScroll}
					setHasShowMore={setHasShowMore}
					setPageIndex={setPageIndex}
					setFormFilters={setFormFilters}
					onSubmitSearch={onSubmitSearch}
					onSubmitExportOrder={onSubmitExportOrder}
					parameterRangeDays={parameterRangeDays}
				/>

				<ShippingCostGrid
					formFilters={formFilters}
					gridData={gridData}
					setGridData={setGridData}
					hasShowMore={hasShowMore}
					setHasShowMore={setHasShowMore}
					pageIndex={pageIndex}
					setPageIndex={setPageIndex}
					isLoadingInfiniteScroll={isLoadingInfiniteScroll}
					setIsLoadingInfiniteScroll={setIsLoadingInfiniteScroll}
				/>
			</ContainerScreen>
		</>
	);
}

export default withQueryparams(ShippingCostScreen);
